import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createObject, deleteSingleObject } from '../../app/reducers/MediaLibrary/mediaLibrarySlice';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { RefreshCcw, Trash2, X } from 'react-feather';
import customId from 'custom-id';
import Webcam from 'react-webcam';
import { bytesToMegaBytes } from '../../utils/Utils';
import { useSelector } from 'react-redux';
import PrimaryButton from '../infrastructure/Buttons/PrimaryButton';
import SecondaryButton from '../infrastructure/Buttons/SecondaryButton';
import { IoEye } from "react-icons/io5";
import DocumentPreview from '../../pages/Products/DocumentPreview';


const FormikDirectFileUploadWithTakePhoto = ({
    name,
    formik,
    label = '',
    required,
    location,
    randomize = false,
    height,
    width,
    afterRemove = null,
    remove = false,
    ...props
}) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const webcamRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [finalString, setFinalString] = useState('');
    const [imgSrc, setImageSrc] = useState(false);
    const [useWebcam, setUseWebcam] = useState(false);
    const [facingMode, setFacingMode] = useState('environment')
    const [showPreview, setShowPreview] = useState(false);
    const [previewKey, setPreviewKey] = useState();
    const isImageFile = (fileName) =>
        /\.(jpg|jpeg|jfif|png|gif|bmp|svg|webp|avif|ico|heif)$/i.test(fileName);
    useEffect(() => {
        if (_.at(formik.values, name)?.[0]) {
            setFinalString(_.at(formik.values, name)[0]);
            if (isImageFile(_.at(formik.values, name)[0])) {
                setImageSrc(true);
            }
        } else {
            // 👈 clear when value is empty
            setFinalString('');
            setImageSrc(false);
        }
    }, [_.at(formik.values, name)?.[0]]);

    const handleUpload = async (file) => {
        if (!file) return;
        if (bytesToMegaBytes(file.size) > 30) {
            formik.setFieldValue(name, null);
            fileInputRef.current.value = '';
            alert('File size too large');
            return;
        }

        setLoading(true);
        try {
            const resp = await dispatch(
                createObject({
                    location,
                    file,
                    fileName: randomize ? `${file.name}${customId({})}` : file.name,
                })
            );

            const key = resp?.payload?.data?.[0]?.Key;
            if (key) {
                setFinalString(key);
                formik.setFieldValue(name, key);
                if (isImageFile(file.name)) setImageSrc(true);
            } else {
                throw new Error('Payload error');
            }
        } catch (err) {
            toast.error(err.message || 'Error uploading file');
        } finally {
            setLoading(false);
        }
    };

    const captureWebcam = async () => {
        const imageSrc = webcamRef.current.getScreenshot({ quality: 1 });
        if (!imageSrc) return;

        const res = await fetch(imageSrc);
        const blob = await res.blob();
        const file = new File([blob], `webcam-${Date.now()}.jpeg`, { type: 'image/jpeg' });

        handleUpload(file);
    };

    return (
        <div className="w-full">
            <DocumentPreview
                previewImg={[previewKey]}
                setPreviewModalOpen={setShowPreview}
                previewModalOpen={showPreview}
            />

            {/* Label */}
            <label
                htmlFor={name}
                className="block text-sm font-semibold text-gray-700 mb-1"
            >
                {label} {required && <span className="text-rose-500">*</span>}
            </label>

            {/* Card */}
            <div className="relative w-full rounded-lg border bg-white p-3 shadow-sm">

                {/* Top actions */}
                <div className="flex items-center justify-between gap-2 mb-3">
                    {!finalString && (
                        <div className="flex gap-2">
                            <button
                                type="button"
                                className={`px-3 py-1.5 text-xs rounded-md border transition
                ${!useWebcam
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                                onClick={() => setUseWebcam(false)}
                            >
                                Upload
                            </button>

                            <button
                                type="button"
                                className={`px-3 py-1.5 text-xs rounded-md border transition
                ${useWebcam
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                                onClick={() => setUseWebcam(true)}
                            >
                                Take Photo
                            </button>
                        </div>
                    )
                        // : (
                        //     <button
                        //         type="button"
                        //         className="ml-auto h-9 w-9 rounded-md bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition"
                        //         onClick={(e) => {
                        //             e.stopPropagation();
                        //             setPreviewKey(`${process.env.REACT_APP_S3_URL}${finalString}`);
                        //             setShowPreview(true);
                        //         }}
                        //     >
                        //         <IoEye size={18} />
                        //     </button>
                        // )
                    }
                </div>

                {/* Content */}
                {finalString ? (
                    <div className="grid grid-cols-[96px_1fr] gap-3 items-center">

                        {/* Preview */}
                        {imgSrc ? (
                            <img
                                src={`${process.env.REACT_APP_S3_URL}${finalString}`}
                                alt="Preview"
                                className="w-24 h-24 rounded-md border object-cover"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewKey(finalString);
                                    setShowPreview(true);
                                }}
                            />
                        ) : (
                            <div className="w-24 h-24 rounded-md border bg-gray-50 flex items-center justify-center text-[11px] text-gray-600 text-center px-1 break-all">
                                {finalString.split("/").pop()}
                            </div>
                        )}

                        {/* Info */}
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                                {finalString.split("/").pop()}
                            </p>

                            {!remove && (
                                <button
                                    type="button"
                                    className="mt-2 inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        const confirm = window.confirm(
                                            "Are you sure you want to delete this file?"
                                        );
                                        if (!confirm) return;

                                        await dispatch(
                                            deleteSingleObject({ keys: [finalString] })
                                        );
                                        setFinalString("");
                                        formik.setFieldValue(name, "");
                                        setImageSrc(false);
                                        afterRemove && setTimeout(() => afterRemove(e), 100);
                                    }}
                                >
                                    <Trash2 size={16} />

                                </button>
                            )}
                        </div>
                    </div>
                ) : loading ? (
                    <div className="flex justify-center py-6">
                        <ClipLoader />
                    </div>
                ) : useWebcam ? (
                    <div className="flex flex-col gap-3">
                        <Webcam
                            audio={false}
                            ref={webcamRef}
                            screenshotFormat="image/jpeg"
                            screenshotQuality={1}
                            videoConstraints={{
                                width: 1280,
                                height: 720,
                                facingMode: facingMode,
                            }}
                            className="rounded-md border"
                        />

                        <div className="flex gap-2">
                            <PrimaryButton type="button" className="px-3 py-1" onClick={captureWebcam}>
                                Capture
                            </PrimaryButton>

                            <SecondaryButton
                                type="button"
                                className="px-2 py-1"
                                onClick={() =>
                                    setFacingMode((p) =>
                                        p === "user" ? "environment" : "user"
                                    )
                                }
                            >
                                <RefreshCcw size={16} />
                            </SecondaryButton>
                        </div>
                    </div>
                ) : (
                    <input
                        ref={fileInputRef}
                        type="file"
                        onChange={(e) => handleUpload(e.currentTarget.files[0])}
                        onBlur={formik.handleBlur}
                        className="w-full text-sm border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        {...props}
                    />
                )}
            </div>

            {/* Error */}
            {formik.errors[name] && formik.touched[name] && (
                <p className="mt-1 text-xs text-red-500">
                    {formik.errors[name]}
                </p>
            )}
        </div>
    );

};

export default FormikDirectFileUploadWithTakePhoto;
